/**
 * Czech bank account numbers and IBANs, pure and offline.
 *
 * A Czech account is `[prefix-]number/bankCode`: a prefix of up to 6 digits, a
 * number of up to 10 and a 4-digit bank code. The Czech National Bank's
 * decree 169/2011 gives both parts a mod-11 check with fixed weights. The IBAN
 * is `CZ` + 2 check digits + bank code + prefix (6, zero-padded) + number
 * (10, zero-padded), with the ISO 13616 mod-97 check.
 */

export interface CzechAccount {
  /** Digits only, no leading zeros; "" when there is none. */
  prefix: string;
  /** Digits only, no leading zeros. */
  number: string;
  /** Four digits. */
  bankCode: string;
}

const NUMBER_WEIGHTS = [6, 3, 7, 9, 10, 5, 8, 4, 2, 1];
const PREFIX_WEIGHTS = [10, 5, 8, 4, 2, 1];

function weightedMod11(digits: string, weights: readonly number[]): boolean {
  const padded = digits.padStart(weights.length, "0");
  let sum = 0;
  for (let i = 0; i < weights.length; i += 1) sum += Number(padded[i]) * weights[i];
  return sum % 11 === 0;
}

function stripZeros(digits: string): string {
  return digits.replace(/^0+(?=\d)/, "");
}

/**
 * `19-2000145399/0800`, `2000145399/0800` or with spaces: the parts, or `null`
 * when the shape is wrong. The check digits are tested by `isValidCzechAccount`.
 */
export function parseCzechAccount(input: string): CzechAccount | null {
  const match = input.replace(/\s+/g, "").match(/^(?:(\d{1,6})-)?(\d{2,10})\/(\d{4})$/);
  if (!match) return null;
  const [, prefix = "", number, bankCode] = match;
  return { prefix: prefix ? stripZeros(prefix) : "", number: stripZeros(number), bankCode };
}

/** The mod-11 checks on the prefix and the number; a number of all zeros is not an account. */
export function isValidCzechAccount(account: CzechAccount): boolean {
  if (/^0*$/.test(account.number)) return false;
  return (
    weightedMod11(account.number, NUMBER_WEIGHTS) &&
    weightedMod11(account.prefix || "0", PREFIX_WEIGHTS)
  );
}

/** `19-2000145399/0800`, or `2000145399/0800` without a prefix. */
export function formatCzechAccount(account: CzechAccount): string {
  const prefix = account.prefix && !/^0+$/.test(account.prefix) ? `${account.prefix}-` : "";
  return `${prefix}${account.number}/${account.bankCode}`;
}

/** ISO 13616 mod 97 of an IBAN-like string (letters as 10–35), computed in chunks. */
function mod97(value: string): number {
  let remainder = 0;
  for (const char of value) {
    const code = /[A-Z]/.test(char) ? String(char.charCodeAt(0) - 55) : char;
    for (const digit of code) remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder;
}

/** Spaces removed, upper case. */
export function compactIban(iban: string): string {
  return iban.replace(/\s+/g, "").toUpperCase();
}

/** Any country's IBAN: shape and the mod-97 check (not each country's own rules). */
export function isValidIban(iban: string): boolean {
  const compact = compactIban(iban);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(compact)) return false;
  return mod97(compact.slice(4) + compact.slice(0, 4)) === 1;
}

/** `CZ65 0800 0000 1920 0014 5399`: groups of four. */
export function formatIban(iban: string): string {
  return compactIban(iban).replace(/(.{4})(?=.)/g, "$1 ");
}

/** The IBAN of a Czech account (the check digits are computed, the account is not validated). */
export function czechAccountToIban(account: CzechAccount): string {
  const bban = `${account.bankCode}${account.prefix.padStart(6, "0")}${account.number.padStart(10, "0")}`;
  const check = 98 - mod97(`${bban}CZ00`);
  return `CZ${String(check).padStart(2, "0")}${bban}`;
}

/** A Czech IBAN back to the account, or `null` for a foreign or invalid IBAN. */
export function ibanToCzechAccount(iban: string): CzechAccount | null {
  const compact = compactIban(iban);
  if (!/^CZ\d{22}$/.test(compact) || !isValidIban(compact)) return null;
  return {
    bankCode: compact.slice(4, 8),
    prefix: /^0+$/.test(compact.slice(8, 14)) ? "" : stripZeros(compact.slice(8, 14)),
    number: stripZeros(compact.slice(14)),
  };
}
